"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { toPng } from "html-to-image";
import { Download, Copy, ChevronDown } from "lucide-react";

type ChartScreenshotContextValue = {
  setPeriod: (key: string) => void;
};

const ChartScreenshotContext =
  createContext<ChartScreenshotContextValue | null>(null);

export function useChartScreenshotPeriod() {
  return useContext(ChartScreenshotContext);
}

/** Прибирає символи, заборонені в іменах файлів */
function sanitizeFileName(name: string, maxLength = 80): string {
  const sanitized = name.replace(/[/\\:*?"<>|]/g, "").trim();
  return sanitized.length > maxLength
    ? sanitized.slice(0, maxLength)
    : sanitized;
}

/** data URL → Blob для clipboard */
function dataUrlToBlob(dataUrl: string): Promise<Blob> {
  return fetch(dataUrl).then((r) => r.blob());
}

type ChartBlockWithScreenshotProps = {
  children: ReactNode;
  className?: string;
  /** Назва гри для імені файлу: "Game Name - {downloadSuffix} - 48h.png" */
  gameName?: string;
  /** Суфікс для імені файлу: "chart" | "reviews" тощо */
  downloadSuffix?: string;
  /** Якщо не передано gameName, використовується це */
  downloadFileName?: string;
  /**
   * Дозволяє перевизначити позицію/розмір кнопки керування графіком.
   * Використовується, коли контент зверху накладається.
   */
  controlsClassName?: string;
};

export function ChartBlockWithScreenshot({
  children,
  className,
  gameName,
  downloadSuffix = "chart",
  downloadFileName = "chart-stats",
  controlsClassName,
}: ChartBlockWithScreenshotProps) {
  const captureRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const periodRef = useRef("48h");

  const setPeriod = useCallback((key: string) => {
    periodRef.current = key;
  }, []);

  const contextValue = useMemo(() => ({ setPeriod }), [setPeriod]);

  const getBaseName = useCallback(
    () =>
      gameName
        ? `${sanitizeFileName(gameName)} - ${downloadSuffix} - ${periodRef.current}`
        : downloadFileName,
    [gameName, downloadFileName, downloadSuffix],
  );

  const capturePng = useCallback(async (): Promise<string | null> => {
    const el = captureRef.current;
    if (!el || isCapturing) return null;
    setIsCapturing(true);
    const prevRadius = (el as HTMLElement).style.borderRadius;
    (el as HTMLElement).style.borderRadius = "0";
    try {
      return await toPng(el, {
        pixelRatio: 2,
        cacheBust: true,
        includeQueryParams: true,
        // Exclude UI chrome marked data-screenshot-ignore (e.g. the
        // "All regions" button) from the exported image.
        filter: (node) =>
          !(
            node instanceof HTMLElement &&
            node.dataset?.screenshotIgnore === "true"
          ),
      });
    } catch (err) {
      console.error("[ChartBlockWithScreenshot]", err);
      return null;
    } finally {
      (el as HTMLElement).style.borderRadius = prevRadius;
      setIsCapturing(false);
    }
  }, [isCapturing]);

  const handleDownload = async () => {
    const dataUrl = await capturePng();
    if (!dataUrl) return;
    const link = document.createElement("a");
    link.href = dataUrl;
    link.download = `${getBaseName()}.png`;
    link.click();
    setDropdownOpen(false);
  };

  const handleCopy = async () => {
    const dataUrl = await capturePng();
    if (!dataUrl) return;
    try {
      const blob = await dataUrlToBlob(dataUrl);
      await navigator.clipboard.write([
        new ClipboardItem({ "image/png": blob }),
      ]);
      setCopied(true);
      setDropdownOpen(false);
    } catch (err) {
      console.error("[ChartBlockWithScreenshot] copy", err);
    }
  };

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);

  useEffect(() => {
    if (!dropdownOpen) return;
    const onOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, [dropdownOpen]);

  return (
    <ChartScreenshotContext.Provider value={contextValue}>
      <div className="relative">
        <div ref={captureRef} className={className}>
          {children}
        </div>
        <div
          ref={dropdownRef}
          className={
            controlsClassName ??
            "absolute top-3 right-3 hidden sm:block origin-top-right"
          }
        >
          {copied ? (
            <span
              className="inline-flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-xs font-medium text-[#66c0f4] bg-[#0f2a3d] border border-[#2a475e]"
              role="status"
            >
              Copied to clipboard
            </span>
          ) : (
            <div className="relative">
              <button
                type="button"
                onClick={() => setDropdownOpen((o) => !o)}
                disabled={isCapturing}
                title="Save chart as image"
                className="flex items-center gap-1 p-2 rounded-lg text-[#8f98a0] hover:text-[#c7d5e0] hover:bg-[#2a475e]/80 transition-colors disabled:opacity-50 disabled:pointer-events-none"
                aria-label="Save chart as image"
                aria-expanded={dropdownOpen}
                aria-haspopup="true"
              >
                {isCapturing ? (
                  <span className="text-xs">...</span>
                ) : (
                  <>
                    <Download
                      className="w-4 h-4 sm:w-5 sm:h-5"
                      strokeWidth={2}
                    />
                    <ChevronDown
                      className={`w-3.5 h-3.5 transition-transform ${dropdownOpen ? "rotate-180" : ""}`}
                      strokeWidth={2}
                    />
                  </>
                )}
              </button>
              {dropdownOpen && (
                <div
                  className="absolute right-0 top-full mt-1 py-1 min-w-[180px] rounded-lg bg-[#16202d] border border-[#2a475e] shadow-lg z-10"
                  role="menu"
                >
                  <button
                    type="button"
                    role="menuitem"
                    onClick={handleDownload}
                    disabled={isCapturing}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[#c7d5e0] hover:bg-[#2a475e]/80 disabled:opacity-50"
                  >
                    <Download className="w-4 h-4 shrink-0" strokeWidth={2} />
                    Download image
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={handleCopy}
                    disabled={isCapturing}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[#c7d5e0] hover:bg-[#2a475e]/80 disabled:opacity-50"
                  >
                    <Copy className="w-4 h-4 shrink-0" strokeWidth={2} />
                    Copy to clipboard
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </ChartScreenshotContext.Provider>
  );
}
